import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {API} from '../../apis/constants';

/**
 * ImageUploader component: allows selecting one image at a time,
 * validates file type and size, shows a preview, and notifies the parent
 * of the current file, preview URL, and any validation error.
 */
export default function ImageUploader({
  onChange,
  label = 'Upload Image',
  sx = {},
  inputId,
  type,
  errorProp,
  binaryImage,
  RestaurantId,
  height,
  width
}) {
  const [preview, setPreview] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (RestaurantId !== 'new' && !preview && binaryImage?.preview) {
      setPreview(API.baseUrl + binaryImage.preview);
    }
    if (onChange) {
      if(errorProp){
        setError(null,errorProp);
      }
      if (error) {
        onChange(null, error); 
      } else if(fileData){
        onChange({ file: fileData, preview }, null); 
      }
    }
  }, [fileData, preview, error, onChange,errorProp]);

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (height && width) {
      const img = new Image();
      img.onload = () => {
      if (img.width > width || img.height > height) {
        setError(`Image dimensions must not exceed ${width}x${height}px`);
        setFileData(null);
        setPreview(null);
      } else {
        setError('');
        setFileData(file);
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(file);
      }
      };
      img.src = URL.createObjectURL(file);
      return;
    }
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Only JPG, JPEG, and PNG files are allowed');
      setFileData(null);
      setPreview(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should not exceed 5MB');
      setFileData(null);
      setPreview(null);
      return;
    }

    setError('');
    setFileData(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setFileData(null);
    setPreview(null);
    setError('');
    onChange({ file: fileData, preview }, null); 
  };

  const id = inputId || `image-uploader-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <Box sx={sx}>
      {preview ? (
        <Box
          sx={{
            marginTop:3,
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200,
            borderRadius: 8,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
          }}
        >
          <img
            src={preview}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        

          <Button
            variant="contained"
            color="secondary"
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              top: 5,
              right: 5,
              minWidth: 30,
              minHeight: 30,
              p: 0,
              fontSize: '0.75rem',
              borderRadius: '50%',
            }}
          >
            X
          </Button>
          

          
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center',marginTop:10 }}>
          <Typography variant="body1" gutterBottom>
            { type == 'country' ?   `New country found: ${label}` :  label}
          </Typography>
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleSelect}
            style={{ display: 'none' }}
            id={id}
          />
          <label htmlFor={id}>
            <Button variant="outlined" component="span">
              {label}
            </Button>
          </label>
          {error && (
            <Typography color="error" variant="body2" style={{ textAlign: 'center' }} >
              {error}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
